import React, { useState } from 'react';

import { getClassNames } from '../../tools/helpers';
import {
  displayValue, durationSeconds, formatCron, formatDuration,
  formatDurationDelta, formatNumberDelta, leafAt, leafLabel, parseDuration,
  replaceLeaf, structuredLeaves,
} from './format';

/**
 * The signed difference between a value and what it would inherit, formatted in
 * the kind both share. Returns null when they match, when they are not
 * comparable, or when the difference is floating-point dust.
 *
 * @param {*} value
 * @param {*} inherited
 * @returns {?string} e.g. "+25m" or "−20".
 */
function deltaBetween(value, inherited) {
  const valueSeconds = durationSeconds(value);
  const inheritedSeconds = durationSeconds(inherited);
  if (valueSeconds !== null && inheritedSeconds !== null) {
    return formatDurationDelta(valueSeconds - inheritedSeconds);
  }

  if (typeof value === 'number' && typeof inherited === 'number') {
    return formatNumberDelta(value - inherited);
  }

  return null;
}

/**
 * The leaves of a structured knob whose values differ from what they would
 * inherit. A structured knob is overridden as a whole — the store replaces the
 * value rather than merging it — so several members can be carried along
 * unchanged by an override of one. Naming those would report deviations the
 * operator did not make.
 *
 * Compared leaf by leaf rather than member by member because a member can hold
 * a GROUP (the per-car-type costs): two groups that are equal value-for-value
 * are still different objects, so comparing members would report all five car
 * types as deviations on a page where nothing was overridden at all.
 *
 * @param {Object} value
 * @param {Object} inherited
 * @returns {Array<{path: Array<string>, value: *}>} in the value's own order.
 */
function differingLeaves(value, inherited) {
  return structuredLeaves(value).filter((leaf) => leaf.value !== leafAt(inherited, leaf.path));
}

/**
 * The comparison line under a field: what the layer above holds, and by how
 * much this value departs from it.
 *
 * Provenance is shown as a comparison rather than a badge because a badge
 * reading "overridden" tells an operator that a value was changed without
 * telling them what it was changed FROM — which is the only thing that lets
 * them judge whether the change is sane before a launch run.
 *
 * @param {Object} props
 * @param {Object} props.knob - One entry from the controller's knobs_payload.
 * @param {string} props.baseline - What the layer above is called here.
 * @returns {JSX.Element}
 */
function ComparisonLine({ knob, baseline }) {
  const {
    key, type, value, inherited_value: inherited, overridden,
  } = knob;
  const testId = `gmpro-knob-${key}-comparison`;

  // A scope can hold a row whose value happens to equal what it would inherit.
  // The card is highlighted (a row IS held here) while the line reads "Same as
  // Global", which contradicts it. Both facts are true and both matter: the row
  // pins this value, so the scope will NOT follow the layer above if that layer
  // changes later. Say that instead of leaving the operator to reconcile a
  // highlight against a label.
  const sameLabel = overridden
    ? `Set here — currently the same as ${baseline}`
    : `Same as ${baseline}`;

  if (type === 'structured') {
    const leaves = differingLeaves(value, inherited);

    if (leaves.length === 0) {
      return (
        <p className="gmpro-knob__same" data-testid={testId}>{sameLabel}</p>
      );
    }

    // One leaf's inherited value anchors the line. Showing the whole inherited
    // object would restate data already on screen field by field.
    const anchor = leaves[0];

    return (
      <p className="gmpro-knob__diff" data-testid={testId}>
        <span className="gmpro-knob__inherited">
          {baseline} {displayValue(leafAt(inherited, anchor.path))}
        </span>
        {leaves.map((leaf) => {
          const delta = deltaBetween(leaf.value, leafAt(inherited, leaf.path));

          return (
            <span className="gmpro-knob__delta" key={leaf.path.join('-')}>
              {' · '}{leaf.path.join(' ')} {delta || displayValue(leaf.value)}
            </span>
          );
        })}
      </p>
    );
  }

  if (value === inherited) {
    return <p className="gmpro-knob__same" data-testid={testId}>{sameLabel}</p>;
  }

  // A nullable knob — the cron is the only one — can have nothing above it to
  // inherit. "Global null" is what rendering that raw looks like; what it means
  // is that this scope is the only one that schedules anything.
  if (inherited === null || inherited === undefined) {
    return (
      <p className="gmpro-knob__diff" data-testid={testId}>
        <span className="gmpro-knob__delta">Set here only — no {baseline} value</span>
      </p>
    );
  }

  const delta = deltaBetween(value, inherited);

  return (
    <p className="gmpro-knob__diff" data-testid={testId}>
      <span className="gmpro-knob__inherited">
        {baseline} {displayValue(inherited)}
      </span>
      {delta && <span className="gmpro-knob__delta">{' · '}{delta}</span>}
    </p>
  );
}

/**
 * One editable input. Durations are edited in their human form and reported
 * back in seconds; anything else is reported as typed. A half-typed duration
 * ("2h" on the way to "2h 00m") stays on screen but is not reported as a value,
 * so the field does not fight the person filling it in.
 *
 * @param {Object} props
 * @param {string} props.id
 * @param {string} props.label
 * @param {*} props.value
 * @param {function(*): void} props.onCommit - called with the storable value.
 * @returns {JSX.Element}
 */
function ValueInput({ id, label, value, onCommit }) {
  const isDuration = durationSeconds(value) !== null;
  // `value ?? ''` — the cron field is legitimately empty (nothing scheduled),
  // and String(null) would seed the input with the literal word "null".
  const [text, setText] = useState(isDuration ? formatDuration(durationSeconds(value)) : String(value ?? ''));

  function handleChange(event) {
    const next = event.target.value;
    setText(next);

    if (!isDuration) {
      // A numeric field commits only a real number. Number('4x') is NaN, which
      // serialises to JSON null — and a null on a non-nullable knob DELETES
      // that layer's override while the save toasts success. Number('') is 0,
      // which would turn "I cleared the field" into "midnight". Either way the
      // half-typed text stays on screen, exactly like a half-typed duration.
      if (typeof value === 'number') {
        const parsed = Number(next);
        if (next.trim() !== '' && Number.isFinite(parsed)) onCommit(parsed);
        return;
      }

      onCommit(next);
      return;
    }

    const seconds = parseDuration(next);
    if (seconds !== null) onCommit(`${seconds}s`);
  }

  return (
    <input
      className="gmpro-knob__input"
      id={id}
      aria-label={label}
      value={text}
      onChange={handleChange}
      data-testid={`gmpro-knob-input-${id}`}
    />
  );
}

/**
 * One knob: its plain-language label, the raw key it is stored under, what it
 * does, the value, and how that value compares to what it would otherwise
 * inherit.
 *
 * The raw key sits between the label and the description deliberately. It is
 * the handle that connects this page to GmproConfiguration.knob, the API
 * payload and the logs — an operator reading a log line needs to find the field
 * that produced it, and the plain-language label alone will not lead them there.
 *
 * @param {Object} props
 * @param {Object} props.knob - One entry from the controller's knobs_payload:
 *   { key, label, description, type, value, inherited_value, overridden }.
 * @param {function(string, *): void} props.onChange - called with the knob key
 *   and its whole new value; a structured knob reports every member, not just
 *   the edited one, because the store replaces the value rather than merging it.
 * @param {string} [props.baseline] - what the layer above is called. "Global"
 *   on a region page; "Default" on the Global page, where there is no layer
 *   above and the catalogue default is what a blank field falls back to.
 * @returns {JSX.Element}
 */
export default function KnobField({ knob, onChange, baseline = 'Global' }) {
  const {
    key, label, description, type, value, overridden,
  } = knob;

  const isCron = key === 'daily_submit_cron';
  const cronSummary = isCron ? formatCron(value) : null;

  return (
    <div
      className={getClassNames('gmpro-knob', {overridden})}
      data-testid={`gmpro-knob-${key}`}
    >
      <div className="gmpro-knob__heading" data-testid={`gmpro-knob-${key}-heading`}>
        <label className="gmpro-knob__label" htmlFor={`gmpro-knob-${key}`}>{label}</label>
        <code className="gmpro-knob__key">{key}</code>
        <p className="gmpro-knob__description">{description}</p>
      </div>

      <div className="gmpro-knob__value">
      {type === 'structured' ? (
        <div className="gmpro-knob__members">
          {structuredLeaves(value).map((leaf) => {
            // One expression, used as both the visible label and the input's
            // aria-label. Written twice, the two drift apart the first time the
            // humanization rule changes.
            const memberLabel = leafLabel(leaf.path);
            const memberId = `gmpro-knob-${key}-${leaf.path.join('-')}`;

            return (
              <div className="gmpro-knob__member" key={memberId}>
                <label className="gmpro-knob__member-label" htmlFor={memberId}>
                  {memberLabel}
                </label>
                <ValueInput
                  id={memberId}
                  label={memberLabel}
                  value={leaf.value}
                  onCommit={(next) => onChange(key, replaceLeaf(value, leaf.path, next))}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="gmpro-knob__single">
          <ValueInput
            id={`gmpro-knob-${key}`}
            label={label}
            value={value}
            onCommit={(next) => onChange(key, next)}
          />
          {durationSeconds(value) !== null && (
            <span className="gmpro-knob__stored">{value}</span>
          )}
          {cronSummary && <span className="gmpro-knob__stored">{cronSummary}</span>}
        </div>
      )}

        <ComparisonLine knob={knob} baseline={baseline} />
      </div>
    </div>
  );
}
